<template>
  <div v-if="showBanner && !settingsOpen" class="cookie-consent cookie-consent_banner" role="dialog">
    <p class="cookie-consent__text">
      {{ isAdminLocalization ? adminText("shellAll.cookie_cookieMessage", "We use cookies for the proper functioning of this website and to improve your user experience. More information can be found at") : $t('cookieMessage') }}
      <a :href="policyURL" target="_blank">{{ isAdminLocalization ? adminText("shellAll.cookie_cookiePrivacySettings", "Privacy Settings") : $t('cookiePrivacySettings') }}</a>.
    </p>
    <div class="cookie-consent__buttons">
      <button type="button" class="cookie-consent__button" @click="save(false, false)">
        {{ isAdminLocalization ? adminText("shellAll.cookie_cookieRejectAll", "REJECT ALL") : $t('cookieRejectAll') }}
      </button>
      <button type="button" class="cookie-consent__button" @click="openSettings">
        {{ isAdminLocalization ? adminText("shellAll.cookie_cookieAdjustSettings", "ADJUST SETTINGS") : $t('cookieAdjustSettings') }}
      </button>
      <button type="button" class="cookie-consent__button" @click="save(true, true)">
        {{ isAdminLocalization ? adminText("shellAll.cookie_cookieAcceptAll", "ACCEPT ALL") : $t('cookieAcceptAll') }}
      </button>
    </div>
  </div>

  <div v-else-if="settingsOpen" class="cookie-consent__overlay">
    <div class="cookie-consent cookie-consent_modal" role="dialog" aria-modal="true">
      <p class="cookie-consent__title">{{ isAdminLocalization ? adminText("shellAll.cookie_cookies", "Cookies") : $t('cookies') }}</p>
      <p class="cookie-consent__text">
        {{ isAdminLocalization ? adminText("shellAll.cookie_cookieMessage", "We use cookies for the proper functioning of this website and to improve your user experience. More information can be found at") : $t('cookieMessage') }}
        <a :href="policyURL" target="_blank">{{ isAdminLocalization ? adminText("shellAll.cookie_cookiePrivacySettings", "Privacy Settings") : $t('cookiePrivacySettings') }}</a>.
      </p>

      <div class="cookie-consent__category">
        <div class="cookie-consent__category-title">
          <span>{{ isAdminLocalization ? adminText("shellAll.cookie_necessaryCookies", "Necessary cookies") : $t('necessaryCookies') }}</span>
          <span>{{ isAdminLocalization ? adminText("shellAll.cookie_cookieAlwaysIncluded", "always included") : $t('cookieAlwaysIncluded') }}</span>
        </div>
        <p class="cookie-consent__text">{{ isAdminLocalization ? adminText("shellAll.cookie_necessaryCookiesMessage", "Necessary cookies are those that the Portal needs in order for it to fully and functionally perform all procedures for Event displaying and Tickets purchasing. This type of cookie cannot be disabled independently.") : $t('necessaryCookiesMessage') }}</p>
      </div>
      <div class="cookie-consent__category">
        <label class="cookie-consent__category-title">
          <span>{{ isAdminLocalization ? adminText("shellAll.cookie_analyticsCookies", "Analytics cookies") : $t('analyticsCookies') }}</span>
          <input v-model="analytics" type="checkbox" />
        </label>
        <p class="cookie-consent__text">{{ isAdminLocalization ? adminText("shellAll.cookie_analyticsCookiesMessage", "Help us track visits and how the Portal is used (Google Analytics) so that we can improve it. They are only enabled with your consent.") : $t('analyticsCookiesMessage') }}</p>
      </div>
      <div class="cookie-consent__category">
        <label class="cookie-consent__category-title">
          <span>{{ isAdminLocalization ? adminText("shellAll.cookie_marketingCookies", "Marketing cookies") : $t('marketingCookies') }}</span>
          <input v-model="marketing" type="checkbox" />
        </label>
        <p class="cookie-consent__text">{{ isAdminLocalization ? adminText("shellAll.cookie_marketingCookiesMessage", "Used by CoreEvent and, on the pages of individual events, by the organizers of those events (via Meta Pixel and Google Ads) to measure ad performance and show ads for events on other platforms, such as Facebook and Instagram. Data may be transferred to advertising service providers, including transfers to the USA. They are only enabled with your consent.") : $t('marketingCookiesMessage') }}</p>
      </div>

      <div class="cookie-consent__buttons">
        <button type="button" class="cookie-consent__button" @click="save(false, false)">
          {{ isAdminLocalization ? adminText("shellAll.cookie_cookieRejectOptional", "REJECT OPTIONAL") : $t('cookieRejectOptional') }}
        </button>
        <button type="button" class="cookie-consent__button" @click="save(analytics, marketing)">
          {{ isAdminLocalization ? adminText("shellAll.cookie_cookieSaveSelection", "SAVE SELECTION") : $t('cookieSaveSelection') }}
        </button>
        <button type="button" class="cookie-consent__button" @click="save(true, true)">
          {{ isAdminLocalization ? adminText("shellAll.cookie_cookieAcceptAll", "ACCEPT ALL") : $t('cookieAcceptAll') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import { EventBus } from '@/plugins/event-bus.js'
import { landingURL } from '@/utils'

// Cookie banner and settings modal. Storing the choice and loading tracking
// scripts is handled by public/js/consent.js (window.ceConsent).
// The banner shows only on purchase pages (routes with meta.cookieBanner); the
// settings modal opens anywhere from the "Cookie settings" link in the footer.
export default {
  mixins: [AdminContext],
  name: 'cookie-consent',
  data: () => ({
    hasConsent: !!(window.ceConsent && window.ceConsent.get()),
    settingsOpen: false,
    analytics: false,
    marketing: false,
  }),
  computed: {
    showBanner() {
      return !this.hasConsent && this.$route.matched.some(route => route.meta.cookieBanner)
    },
    policyURL() {
      const lng = this.isAdminLocalization ? this.$adminLocale() : this.$i18next.resolvedLanguage || this.$i18next.language
      return lng === 'hr' ? `${landingURL}/personal-data/` : `${landingURL}/en/personal-data/`
    },
  },
  mounted() {
    EventBus.$on('open-cookie-settings', this.openSettings)
  },
  destroyed() {
    EventBus.$off('open-cookie-settings', this.openSettings)
  },
  methods: {
    openSettings() {
      const consent = (window.ceConsent && window.ceConsent.get()) || {}
      this.analytics = !!consent.analytics
      this.marketing = !!consent.marketing
      this.settingsOpen = true
    },
    save(analytics, marketing) {
      if (!window.ceConsent) return

      this.hasConsent = true
      this.settingsOpen = false
      window.ceConsent.save({ analytics, marketing })
    },
  },
}
</script>

<style scoped>
.cookie-consent {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cookie-consent_banner {
  position: fixed;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  max-width: 960px;
  margin: 0 auto;
  z-index: 1000;
}

.cookie-consent__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
}

.cookie-consent_modal {
  max-width: 640px;
  max-height: 100%;
  overflow-y: auto;
}

.cookie-consent__title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.cookie-consent__text {
  font-size: 0.875rem;
  margin: 0;
}

.cookie-consent__text a {
  text-decoration: underline;
}

.cookie-consent__category {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 1rem;
  border-top: 1px dashed #97979780;
}

.cookie-consent__category-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
}

.cookie-consent__category-title input {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
}

.cookie-consent__buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}

/* All choices look the same, so rejecting is as easy as accepting. */
.cookie-consent__button {
  background: #0277bd;
  color: white;
  border-radius: 0.5rem;
  padding: 0.625rem 1.25rem;
  font-weight: 700;
  font-size: 0.875rem;
}

.cookie-consent__button:hover {
  opacity: 0.85;
}

@media (max-width: 576px) {
  .cookie-consent__buttons {
    flex-direction: column;
  }
}
</style>
